/**
 * Cele doua feluri in care portalul spune ceva scurt, fara jQuery:
 *
 *  - `toast()` scrie ce scria toastr cu optiunile din general.js (colt dreapta
 *    jos, cel mai nou sus, buton de inchis, bara de progres, 10 s, se inchide
 *    la apasare, sta cat e mouse-ul pe el si mai ramane 1 s dupa). Foaia lui
 *    e in app.css (#toast-container), deci markupul trebuie sa fie al lui.
 *
 *  - `newAlert()` e functia din general.js: un `.alert.alert-{type}` in
 *    `.notifications`, scos dupa `alive` ms cu un fade de 200 ms.
 */

const TOAST_SHOW = 300
const TOAST_HIDE = 1000
const TOAST_TIMEOUT = 10_000
const TOAST_EXTENDED = 1000

function container(): HTMLElement {
  let box = document.getElementById('toast-container')
  if (!box) {
    box = document.createElement('div')
    box.id = 'toast-container'
    box.className = 'toast-bottom-right'
    box.setAttribute('aria-live', 'polite')
    box.setAttribute('role', 'alert')
    document.body.appendChild(box)
  }
  return box
}

export function toast(message: string, title: string, kind: 'success' | 'error' | 'info' | 'warning' = 'success'): HTMLElement {
  const box = container()
  const el = document.createElement('div')
  el.className = `toast toast-${kind}`
  el.setAttribute('aria-live', kind === 'success' || kind === 'info' ? 'polite' : 'assertive')
  el.style.opacity = '0'
  el.style.display = 'block'
  el.style.transition = `opacity ${TOAST_SHOW}ms`

  const progress = document.createElement('div')
  progress.className = 'toast-progress'
  progress.style.width = '100%'
  el.appendChild(progress)

  const close = document.createElement('button')
  close.type = 'button'
  close.className = 'toast-close-button'
  close.setAttribute('role', 'button')
  close.innerHTML = '&times;'
  el.appendChild(close)

  if (title) {
    const t = document.createElement('div')
    t.className = 'toast-title'
    t.textContent = title
    el.appendChild(t)
  }
  const m = document.createElement('div')
  m.className = 'toast-message'
  m.textContent = message
  el.appendChild(m)

  box.prepend(el)
  requestAnimationFrame(() => {
    el.style.opacity = '1'
  })

  let hideTimer: ReturnType<typeof setTimeout> | null = null
  let progressTimer: ReturnType<typeof setInterval> | null = null
  let hideAt = 0
  let maxHide = 0
  let gone = false

  const stopProgress = () => {
    if (progressTimer) clearInterval(progressTimer)
    progressTimer = null
  }
  const hide = () => {
    if (gone) return
    gone = true
    if (hideTimer) clearTimeout(hideTimer)
    stopProgress()
    el.style.transition = `opacity ${TOAST_HIDE}ms`
    el.style.opacity = '0'
    setTimeout(() => {
      el.remove()
      if (box.childElementCount === 0) box.remove()
    }, TOAST_HIDE)
  }
  const arm = (ms: number) => {
    if (hideTimer) clearTimeout(hideTimer)
    hideTimer = setTimeout(hide, ms)
    hideAt = Date.now() + ms
    maxHide = ms
    stopProgress()
    progressTimer = setInterval(() => {
      const left = ((hideAt - Date.now()) / maxHide) * 100
      progress.style.width = `${Math.max(0, left)}%`
    }, 10)
  }

  arm(TOAST_TIMEOUT)
  el.addEventListener('mouseenter', () => {
    if (hideTimer) clearTimeout(hideTimer)
    hideTimer = null
    stopProgress()
    progress.style.width = '100%'
  })
  el.addEventListener('mouseleave', () => {
    if (!gone) arm(TOAST_EXTENDED)
  })
  el.addEventListener('click', hide)
  close.addEventListener('click', (ev) => {
    ev.stopPropagation()
    hide()
  })

  return el
}

export function newAlert(message: string, type: string, alive = 5000, box?: Element | null): HTMLElement {
  const alert = document.createElement('div')
  alert.className = `alert alert-${type}`
  alert.innerHTML = message
  const target = box ?? document.querySelector('.notifications') ?? document.body
  target.appendChild(alert)
  setTimeout(() => {
    alert.style.transition = 'opacity 200ms'
    alert.style.opacity = '0'
    setTimeout(() => alert.remove(), 200)
  }, alive)
  return alert
}
